import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
  type ReactNode,
} from "react";
import { useToast } from "@hightouch/htui";
import {
  isAdobeFontStylesheetUrl,
  type HostedFontFamily,
} from "@hightouch/lib/brand-profiles/assets/adobe-fonts";
import { acquireHostedFontStylesheet } from "src/pages/ad-studio/hooks/acquire-hosted-font-stylesheet";
import { oneFontFacePerAddress } from "src/pages/ad-studio/hooks/one-font-face-per-address";
import { waitForHostedFontFaces } from "src/pages/ad-studio/hooks/wait-for-hosted-font-faces";
import { storageFontName } from "@hightouch/lib/brand-profiles/assets/font-names";
import {
  PLATFORM_FONTS,
  canonicalFamilyKey,
} from "@hightouch/lib/brand-profiles/assets/platform-fonts";

// Support fonts from ad studio (files) and LCM (google, custom, web-safe)
export type InjectableFont =
  // `fileId` is the private S3 storage key, carried so consumers (e.g. the LCM
  // email picker) can promote the bytes to the public content bucket at
  // apply-time. `url` is the session-authed `/api/agents/files` URL, which
  // 401s for email recipients. `publicUrl` is the content-addressed public
  // bucket copy — when present it's preferred for on-screen byte loading
  // (fetched anonymously, so it works regardless of which origin holds the
  // session cookie and of the CDN's CORS credentials policy).
  //
  // `fallbackFont` is the brand-configured CSS fallback family (e.g. "Georgia")
  // for brand web fonts. The email picker appends it as the fallback chain when
  // a client can't load the web font; web-safe families need no fallback so
  // don't carry one.
  // `family`/`weight`/`style` are the typographic identity parsed from the
  // font binary at upload (sanitized server-side). When present, the face is
  // registered under `family` with weight/style descriptors — so files like
  // Geist-Black.woff2 resolve `font-family: "Geist"; font-weight: 900` — in
  // addition to the filename-derived `name` alias that existing content
  // references.
  | {
      type: "file";
      name: string;
      url: string;
      publicUrl?: string;
      fileId?: string;
      fallbackFont?: string;
      family?: string;
      weight?: string;
      style?: string;
    }
  | {
      type: "google";
      name: string;
      variants?: string[];
      fallbackFont?: string;
      /**
       * "platform" marks a pool family appended by `withPlatformFonts` rather
       * than one the brand declared. Pool families are loaded for on-screen
       * rendering but only inlined into exports when the markup references
       * them: the pool is ~80 faces, and Google serves each as several
       * unicode-range subsets, so inlining it wholesale adds ~20 MB of base64
       * to every export.
       */
      origin?: "platform";
    }
  | {
      type: "custom";
      name: string;
      variants: { url: string; weight: string; style: string }[];
      fallbackFont?: string;
    }
  | (HostedFontFamily & { fallbackFont?: string })
  | { type: "web-safe"; name: string };

interface FontInjectionContextValue {
  fontOptions: { label: string; value: string }[];
  /**
   * Platform pool families (Arimo, Roboto, ...) that are LOADED but not part
   * of the brand's `fontOptions`. The picker offers them in their own group
   * so a human can choose the same faces the agent writes into markup.
   */
  globalFontOptions: { label: string; value: string }[];
  /**
   * True once all font-loading attempts finish, including failures. Per-ad
   * readiness uses `loadedFamilies` so unrelated fonts cannot block the editor.
   */
  fontsReady: boolean;
  /**
   * Build export font CSS: Adobe uses hosted imports for the browser renderer;
   * other fonts use self-contained base64 rules. Inlines ALL binary brand fonts rather than only
   * the families the markup names: a brand font referenced by a display name
   * that doesn't match its hashed upload basename would otherwise be dropped
   * and silently render in a system font. Platform pool families are the
   * exception and are inlined only when `markup` references them (see
   * `InjectableFont.origin`). Results are cached per font, so repeated exports
   * are cheap.
   */
  getFontRulesForMarkup: (markup: string) => Promise<string[]>;
  /**
   * Build CSS for ALL brand fonts, retaining Adobe as hosted imports. For consumers that
   * can't scope to a single markup string (e.g. the sandboxed composition
   * iframe, which renders many layers internally). Platform pool families are
   * excluded, since without markup there is nothing to scope them by. Cached
   * per font.
   */
  getAllFontRules: () => Promise<string[]>;
  /**
   * Lowercased names of every brand font supplied to the provider, so
   * consumers can tell which referenced families are brand fonts worth waiting
   * for vs. system/fallback families that are always available.
   */
  brandFamilies: ReadonlySet<string>;
  /**
   * Lowercased names of brand fonts whose faces have finished loading for
   * on-screen rendering. Grows as fonts resolve; pair with `brandFamilies`
   * via `useFontsReadyForMarkup` to gate per-image instead of on all fonts.
   */
  loadedFamilies: ReadonlySet<string>;
  /**
   * Registered face weights per uploaded font, keyed by lowercased
   * typographic family AND filename-derived alias. Lets weight pickers
   * offer only weights that resolve to a real face; names absent here
   * (google/custom/web-safe, or variable-font ranges) have no restriction.
   */
  fileFontWeightsByName: ReadonlyMap<string, string[]>;
  fileAliasToFamily: ReadonlyMap<string, string>;
}

// ── Helpers ──

function arrayBufferToBase64(buffer: ArrayBuffer): string {
  const bytes = new Uint8Array(buffer);
  let binary = "";
  for (let i = 0; i < bytes.length; i++) {
    binary += String.fromCharCode(bytes[i]!);
  }
  return btoa(binary);
}

function parseGoogleFontVariant(
  variant: string,
): { weight: string; italic: boolean } | null {
  const v = variant.toLowerCase().trim();
  if (v === "regular") return { weight: "400", italic: false };
  if (v === "italic") return { weight: "400", italic: true };
  const match = v.match(/^(\d{3})(italic)?$/);
  if (match) return { weight: match[1]!, italic: !!match[2] };
  return null;
}

// Get a CSS compatible Google Fonts URL for a font name and variants
export function constructGoogleFontUrl(
  fontName: string,
  variants?: string[],
): string {
  const urlSafeName = encodeURIComponent(fontName).replace(/%20/g, "+");

  if (!variants || variants.length === 0) {
    return `https://fonts.googleapis.com/css2?family=${urlSafeName}&display=swap`;
  }

  const parsed = variants
    .map(parseGoogleFontVariant)
    .filter((p): p is { weight: string; italic: boolean } => p !== null);

  if (parsed.length === 0) {
    return `https://fonts.googleapis.com/css2?family=${urlSafeName}&display=swap`;
  }

  const hasItalic = parsed.some((p) => p.italic);

  if (hasItalic) {
    const tuples = new Set<string>();
    for (const p of parsed) {
      tuples.add(`${p.italic ? "1" : "0"},${p.weight}`);
    }
    const sortedTuples = [...tuples].sort();
    return `https://fonts.googleapis.com/css2?family=${urlSafeName}:ital,wght@${sortedTuples.join(";")}&display=swap`;
  }

  const uniqueWeights = [...new Set(parsed.map((p) => p.weight))].sort();
  return `https://fonts.googleapis.com/css2?family=${urlSafeName}:wght@${uniqueWeights.join(";")}&display=swap`;
}

/**
 * Fetch a font file's raw bytes. Session-authed brand-font URLs are app
 * endpoints that need the session cookie, so those are fetched with
 * credentials and the `stream=true` flag rather than relying on a CSS `url()`
 * (which fetches anonymously and would 401). Public-bucket URLs are fetched
 * anonymously — a credentialed fetch is rejected outright when the CDN answers
 * `Access-Control-Allow-Origin: *`. Returns null on any failure so a single
 * bad font can't block the rest.
 */
async function fetchFontBytes(
  url: string,
  anonymous: boolean,
): Promise<ArrayBuffer | null> {
  try {
    if (anonymous) {
      const res = await fetch(url);
      if (!res.ok) return null;
      return await res.arrayBuffer();
    }
    const streamUrl = url.includes("?")
      ? `${url}&stream=true`
      : `${url}?stream=true`;
    const res = await fetch(streamUrl, { credentials: "include" });
    if (!res.ok) return null;
    return await res.arrayBuffer();
  } catch {
    return null;
  }
}

/**
 * The URL a file font's bytes should be loaded from for rendering/inlining:
 * the anonymous public copy when one exists, else the session-authed URL.
 */
function fileFontAliases(
  font: Extract<InjectableFont, { type: "file" }>,
): string[] {
  const aliases: string[] = [];
  if (!font.family || font.family !== font.name) {
    aliases.push(font.name);
  }
  const storageName = storageFontName(font.fileId, font.name);
  if (storageName && storageName !== font.name && storageName !== font.family) {
    aliases.push(storageName);
  }
  return aliases;
}

// Saved markup can use filename aliases while grouped pickers list the parsed family.
export function fileAliasToFamilyMap(
  fonts: readonly InjectableFont[],
): Map<string, string> {
  const aliases = new Map<string, string>();
  for (const font of fonts) {
    if (font.type !== "file" || !font.family) continue;
    aliases.set(font.family.toLowerCase(), font.family);
    for (const alias of fileFontAliases(font)) {
      aliases.set(alias.toLowerCase(), font.family);
    }
  }
  return aliases;
}

export function fontPickerSelection(
  families: readonly string[],
  fallbackFamily: string,
  aliases: ReadonlyMap<string, string>,
): { value: string; mixed: boolean } {
  const normalize = (family: string) =>
    aliases.get(family.toLowerCase()) ?? family;
  // Inline runs using "Knockout-69" and "Knockout" share one font, even if the layer default is "Inter".
  // Normalize before counting families so the picker shows that shared font.
  const normalized = [...new Set(families.map(normalize))];
  const fallback = normalize(fallbackFamily);
  const [first] = normalized;
  // Fall back to the layer font when there are no runs or several distinct families.
  // For several families, the picker displays "Mixed" instead of this fallback value.
  return {
    value: normalized.length === 1 ? (first ?? fallback) : fallback,
    mixed: normalized.length > 1,
  };
}

function fileFaceDescriptors(
  font: Extract<InjectableFont, { type: "file" }>,
): FontFaceDescriptors | undefined {
  if (!font.weight && !font.style) return undefined;
  return {
    ...(font.weight ? { weight: font.weight } : {}),
    ...(font.style ? { style: font.style } : {}),
  };
}

function fileFontSource(font: Extract<InjectableFont, { type: "file" }>): {
  url: string;
  anonymous: boolean;
} {
  return font.publicUrl
    ? { url: font.publicUrl, anonymous: true }
    : { url: font.url, anonymous: false };
}

/**
 * Fetch Google Fonts CSS, then inline each referenced font file as a base64
 * data URL so the resulting @font-face rules are self-contained (needed for
 * SVG export where external URLs can't be resolved).
 */
async function fetchAndInlineGoogleFontRules(
  fontName: string,
  variants?: string[],
): Promise<string[]> {
  try {
    const cssUrl = constructGoogleFontUrl(fontName, variants);
    const res = await fetch(cssUrl);
    if (!res.ok) return [];
    const cssText = await res.text();

    // Extract individual @font-face blocks
    const blocks = cssText.match(/@font-face\s*\{[^}]+\}/g);
    if (!blocks) return [];

    const inlinedRules = await Promise.all(
      blocks.map(async (block) => {
        // Replace each url(...) reference with an inlined base64 data URL
        const urlMatch = block.match(/url\(([^)]+)\)/);
        if (!urlMatch) return block;

        const fontUrl = urlMatch[1]!.replace(/['"]/g, "");
        try {
          const fontRes = await fetch(fontUrl);
          if (!fontRes.ok) return block;
          const buf = await fontRes.arrayBuffer();
          const ext = fontUrl.includes(".woff2")
            ? "font/woff2"
            : fontUrl.includes(".woff")
              ? "font/woff"
              : "application/octet-stream";
          const dataUrl = `data:${ext};base64,${arrayBufferToBase64(buf)}`;
          return block.replace(/url\([^)]+\)/, `url("${dataUrl}")`);
        } catch {
          return block;
        }
      }),
    );

    return inlinedRules;
  } catch {
    return [];
  }
}

// Matches the primary (first) family in a CSS `font-family:` declaration,
// handling double-quoted, single-quoted, and unquoted names. The quoted
// alternatives must come first so a quoted family isn't truncated, and the
// unquoted alternative stops at the comma/terminator so it can't overrun into
// surrounding markup (e.g. the closing `"` of a style attribute).
const FONT_FAMILY_DECL_RE =
  /font-family\s*:\s*(?:"([^"]+)"|'([^']+)'|([^,;{}"']+))/gi;
const FONT_FAMILY_ATTR_RE = /font-family\s*=\s*["']([^"']+)["']/gi;

export function isPlatformFont(font: InjectableFont): boolean {
  return font.type === "google" && font.origin === "platform";
}

/**
 * The fonts an export inlines: every brand font (see `getFontRulesForMarkup`
 * for why brand fonts are not scoped by reference), plus the platform pool
 * families `markup` names. Pool families are addressed by their exact Google
 * family name, so the markup scan is reliable for them, and the pool is far
 * too large to inline unconditionally. Adobe imports are also scoped to referenced
 * families so unused fonts do not force server rendering. Without markup the pool is excluded.
 */
export function selectFontsToInline(
  fonts: InjectableFont[],
  markup?: string,
): InjectableFont[] {
  const referenced =
    markup === undefined ? null : extractReferencedFontFamilies(markup);
  return fonts.filter((f) =>
    f.type === "hosted-css"
      ? markup === undefined || referenced?.has(f.name.toLowerCase())
      : !isPlatformFont(f) || (referenced?.has(f.name.toLowerCase()) ?? false),
  );
}

/**
 * Extract the set of font-family names referenced by SVG/HTML markup, so the
 * export path only inlines fonts that are actually used. Reads both the CSS
 * form (`font-family: X, fallback`) and the SVG attribute form
 * (`font-family="X"`), takes the first (primary) family in each list, strips
 * quotes, and normalizes to lowercase for case-insensitive matching against
 * `InjectableFont.name`.
 */
export function extractReferencedFontFamilies(markup: string): Set<string> {
  // `innerHTML` serializes quote characters inside style attributes as
  // entities — `font-family: &quot;Druk XXCond&quot;` — which is how CSSOM
  // writes any multi-word family applied via the picker. Decode them so
  // quoted families match; without this the head reconciler doesn't see the
  // family as referenced and prunes the @font-face it just injected.
  const decoded = markup
    .replace(/&quot;|&#0*34;/gi, '"')
    .replace(/&#0*39;|&apos;/gi, "'");
  const out = new Set<string>();
  const add = (raw: string | undefined) => {
    const first = raw?.split(",")[0]?.replace(/['"]/g, "").trim();
    if (first) out.add(first.toLowerCase());
  };
  for (const m of decoded.matchAll(FONT_FAMILY_DECL_RE)) {
    add(m[1] ?? m[2] ?? m[3]);
  }
  for (const m of decoded.matchAll(FONT_FAMILY_ATTR_RE)) add(m[1]);
  return out;
}

/**
 * Returns true when every brand font referenced by `markup` has finished
 * loading. Families referenced in the markup that aren't brand fonts
 * (web-safe / system / anything not supplied to the provider) are ignored —
 * they render from the system and need no loading. Pure so it can be unit
 * tested without React.
 */
export function areReferencedFontsLoaded(
  markup: string,
  brandFamilies: ReadonlySet<string>,
  loadedFamilies: ReadonlySet<string>,
): boolean {
  for (const family of extractReferencedFontFamilies(markup)) {
    if (brandFamilies.has(family) && !loadedFamilies.has(family)) {
      return false;
    }
  }
  return true;
}

// Stable cache key per font so repeated exports reuse inlined rules.
function fontIdentityKey(font: InjectableFont): string {
  switch (font.type) {
    case "file":
      return `file:${font.url}:${font.family ?? ""}:${font.weight ?? ""}:${font.style ?? ""}`;
    case "google":
      return `google:${font.name}:${(font.variants ?? []).join(",")}`;
    case "hosted-css":
      return `hosted-css:${font.name}:${font.cssUrl}:${font.variants.map((variant) => `${variant.weight}:${variant.style}`).join(",")}`;
    case "custom":
      return `custom:${font.name}:${font.variants.map((v) => v.url).join(",")}`;
    case "web-safe":
      return `web-safe:${font.name}`;
  }
}

/**
 * The registered weight numerically closest to `currentWeight`, or null when
 * the list is empty or already contains it (no snap needed). Pair with
 * `fileFontWeightsByName` when applying a font so the element lands on a face
 * the family actually has instead of a browser-synthesized approximation.
 */
export function nearestWeight(
  weights: readonly string[],
  currentWeight: string,
): string | null {
  if (
    !currentWeight ||
    weights.length === 0 ||
    weights.includes(currentWeight)
  ) {
    return null;
  }
  const target = Number(currentWeight);
  return weights.reduce((best, weight) =>
    Math.abs(Number(weight) - target) < Math.abs(Number(best) - target)
      ? weight
      : best,
  );
}

// ── Context ──

const EMPTY: FontInjectionContextValue = {
  fontOptions: [],
  globalFontOptions: [],
  fontsReady: false,
  getFontRulesForMarkup: async () => [],
  getAllFontRules: async () => [],
  brandFamilies: new Set(),
  loadedFamilies: new Set(),
  fileFontWeightsByName: new Map(),
  fileAliasToFamily: new Map(),
};
const FontInjectionContext = createContext<FontInjectionContextValue>(EMPTY);

/**
 * Provider that loads brand fonts through hosted stylesheets or `FontFace`
 * instances and exposes export CSS without copying Adobe font binaries.
 * Each call site provides its own `fonts` array so
 * the provider is data-agnostic.
 */
export function FontInjectionProvider({
  fonts: requestedFonts,
  pickerFonts,
  groupFileFontsByFamily = false,
  children,
}: {
  /** Earlier fonts win: when two faces share a family, weight and style, only the first one loads under that family. */
  fonts: InjectableFont[];
  /**
   * The subset offered in `fontOptions` — the list a human PICKS from.
   * Defaults to `fonts`.
   *
   * Keep this narrower than `fonts`, never the reverse: `fonts` is the set of
   * faces actually LOADED into the document, and an ad referencing a font that
   * was retired from the brand profile must still render in that face. Passing
   * the narrowed list as `fonts` would break editor preview for those ads.
   */
  pickerFonts?: InjectableFont[];
  /**
   * Collapse uploaded faces with parsed metadata into one `fontOptions`
   * entry per typographic family (value = the family name). Opt-in per
   * surface: LCM turns this on; Ad Studio keeps the flat per-file list
   * in the picker even though faces now also register under the
   * typographic family.
   */
  groupFileFontsByFamily?: boolean;
  children: ReactNode;
}) {
  const { toast } = useToast();
  const fonts = useMemo(
    () => oneFontFacePerAddress(requestedFonts),
    [requestedFonts],
  );
  const linksRef = useRef<HTMLLinkElement[]>([]);
  const facesRef = useRef<FontFace[]>([]);
  // Raw font bytes per file URL — shared by render-face loading and export
  // inlining so each font is fetched at most once.
  const bytesCacheRef = useRef(new Map<string, Promise<ArrayBuffer | null>>());
  // Built base64 @font-face rules per font identity.
  const rulesCacheRef = useRef(new Map<string, Promise<string[]>>());
  const [fontsReady, setFontsReady] = useState(!fonts.length);
  // Lowercased brand font names whose faces have finished loading. Grows as
  // fonts resolve, so consumers can gate on just the fonts an image uses.
  const [loadedFamilies, setLoadedFamilies] = useState<ReadonlySet<string>>(
    () => new Set(),
  );

  // Stable serialisation key so effects/memos only re-run when fonts change.
  const fontsKey = useMemo(() => JSON.stringify(fonts), [fonts]);
  const optionFonts = pickerFonts ?? fonts;
  const optionFontsKey = useMemo(
    () => JSON.stringify(optionFonts),
    [optionFonts],
  );

  // TODO(eric.lovett): This `biome-ignore` was part of a blanket suppression so that we could
  // turn on the rule. It is probably covering up a real bug. Please fix this or replace this
  // TODO with an explanation of why we don't want all deps listed.
  // biome-ignore lint/correctness/useExhaustiveDependencies: legacy suppression, probably a bug
  const brandFamilies = useMemo(() => {
    const families = new Set<string>();
    for (const f of fonts) {
      families.add(f.name.toLowerCase());
      if (f.type === "file" && f.family) families.add(f.family.toLowerCase());
      if (f.type === "file") {
        const storageName = storageFontName(f.fileId, f.name);
        if (storageName) families.add(storageName.toLowerCase());
      }
    }
    return families;
  }, [fontsKey]);

  // TODO(eric.lovett): This `biome-ignore` was part of a blanket suppression so that we could
  // turn on the rule. It is probably covering up a real bug. Please fix this or replace this
  // TODO with an explanation of why we don't want all deps listed.
  // biome-ignore lint/correctness/useExhaustiveDependencies: legacy suppression, probably a bug
  const fileFontWeightsByName = useMemo(() => {
    const weights = new Map<string, string[]>();
    const push = (key: string, weight: string) => {
      const list = weights.get(key) ?? [];
      if (!list.includes(weight)) list.push(weight);
      weights.set(key, list);
    };
    for (const f of fonts) {
      // A variable-font range covers every weight — no restriction to offer.
      if (f.type !== "file" || !f.weight || f.weight.includes(" ")) continue;
      push(f.name.toLowerCase(), f.weight);
      if (f.family) push(f.family.toLowerCase(), f.weight);
      const storageName = storageFontName(f.fileId, f.name);
      if (storageName) push(storageName.toLowerCase(), f.weight);
    }
    for (const list of weights.values()) {
      list.sort((a, b) => Number(a) - Number(b));
    }
    return weights;
  }, [fontsKey]);

  const fileAliasToFamily = useMemo(
    () =>
      groupFileFontsByFamily
        ? fileAliasToFamilyMap(fonts)
        : new Map<string, string>(),
    [fonts, groupFileFontsByFamily],
  );

  const fetchBytes = useCallback(
    (url: string, anonymous = false): Promise<ArrayBuffer | null> => {
      const cache = bytesCacheRef.current;
      let p = cache.get(url);
      if (!p) {
        p = fetchFontBytes(url, anonymous);
        cache.set(url, p);
      }
      return p;
    },
    [],
  );

  const buildRulesForFont = useCallback(
    (font: InjectableFont): Promise<string[]> => {
      const cache = rulesCacheRef.current;
      const key = fontIdentityKey(font);
      let p = cache.get(key);
      if (!p) {
        p = (async (): Promise<string[]> => {
          switch (font.type) {
            case "file": {
              const source = fileFontSource(font);
              const buf = await fetchBytes(source.url, source.anonymous);
              if (!buf) return [];
              const dataUrl = `data:application/octet-stream;base64,${arrayBufferToBase64(buf)}`;
              const rules: string[] = [];
              const weight = font.weight ? ` font-weight: ${font.weight};` : "";
              const style = font.style ? ` font-style: ${font.style};` : "";
              const descriptors = `${weight}${style}`;
              if (font.family) {
                rules.push(
                  `@font-face { font-family: "${font.family}"; src: url("${dataUrl}");${descriptors} }`,
                );
              }
              // Filename / storage-key aliases carry the same weight so
              // Firefox doesn't skip a 400-default face at weight 800.
              for (const alias of fileFontAliases(font)) {
                rules.push(
                  `@font-face { font-family: "${alias}"; src: url("${dataUrl}");${descriptors} }`,
                );
              }
              return rules;
            }
            case "custom": {
              const rules = await Promise.all(
                font.variants.map(async (variant) => {
                  const buf = await fetchBytes(variant.url);
                  if (!buf) return null;
                  const dataUrl = `data:application/octet-stream;base64,${arrayBufferToBase64(buf)}`;
                  return `@font-face { font-family: "${font.name}"; src: url("${dataUrl}"); font-weight: ${variant.weight}; font-style: ${variant.style}; }`;
                }),
              );
              return rules.filter((r): r is string => r !== null);
            }
            case "hosted-css":
              if (!isAdobeFontStylesheetUrl(font.cssUrl))
                throw new Error("Invalid Adobe web project URL");
              return [`@import url("${font.cssUrl}");`];
            case "google":
              return fetchAndInlineGoogleFontRules(font.name, font.variants);
            case "web-safe":
              return [];
          }
        })();
        cache.set(key, p);
      }
      return p;
    },
    [fetchBytes],
  );

  // TODO(eric.lovett): This `biome-ignore` was part of a blanket suppression so that we could
  // turn on the rule. It is probably covering up a real bug. Please fix this or replace this
  // TODO with an explanation of why we don't want all deps listed.
  const buildRulesForFonts = useCallback(
    async (selected: InjectableFont[]): Promise<string[]> => {
      const arrays = await Promise.all(selected.map(buildRulesForFont));
      const rules = [...new Set(arrays.flat())];
      return [
        ...rules.filter((rule) => rule.startsWith("@import")),
        ...rules.filter((rule) => !rule.startsWith("@import")),
      ];
    },
    [buildRulesForFont],
  );

  // biome-ignore lint/correctness/useExhaustiveDependencies: `fontsKey` tracks `fonts` content; the array identity changes every render
  const getAllFontRules = useCallback(
    (): Promise<string[]> => buildRulesForFonts(selectFontsToInline(fonts)),
    [fontsKey, buildRulesForFonts],
  );

  // Inline ALL brand fonts on export rather than only the markup-referenced
  // ones. Selecting by referenced family was fragile: a brand font referenced
  // by a display name that doesn't match its hashed upload basename (e.g.
  // "apercu-bold.e25a8b88") would be dropped, and unlike on-screen preview the
  // export can't fall back to document.fonts, so the glyphs silently rendered
  // in a system font. Export isn't latency-critical and the results are cached,
  // so inlining everything is the simpler, correct choice. Only the platform
  // pool is scoped by reference (see `selectFontsToInline`).
  // biome-ignore lint/correctness/useExhaustiveDependencies: `fontsKey` tracks `fonts` content; the array identity changes every render
  const getFontRulesForMarkup = useCallback(
    (markup: string): Promise<string[]> =>
      buildRulesForFonts(selectFontsToInline(fonts, markup)),
    [fontsKey, buildRulesForFonts],
  );

  // TODO(eric.lovett): This `biome-ignore` was part of a blanket suppression so that we could
  // turn on the rule. It is probably covering up a real bug. Please fix this or replace this
  // TODO with an explanation of why we don't want all deps listed.
  // biome-ignore lint/correctness/useExhaustiveDependencies: legacy suppression, probably a bug
  useEffect(() => {
    // Reset per-font caches and load tracking whenever the font set changes.
    bytesCacheRef.current = new Map();
    rulesCacheRef.current = new Map();
    setLoadedFamilies(new Set());

    if (fonts.length === 0) {
      cleanup();
      setFontsReady(true);
      return;
    }

    setFontsReady(false);
    let canceled = false;
    const releaseStylesheets: (() => void)[] = [];

    const markLoaded = (family: string) => {
      if (canceled) return;
      const lower = family.toLowerCase();
      setLoadedFamilies((prev) => {
        if (prev.has(lower)) return prev;
        const next = new Set(prev);
        next.add(lower);
        return next;
      });
    };

    const loadFace = async (
      family: string,
      source: { url: string; anonymous?: boolean },
      descriptors?: FontFaceDescriptors,
    ): Promise<FontFace | null> => {
      const buf = await fetchBytes(source.url, source.anonymous);
      if (!buf) return null;
      try {
        const face = new FontFace(family, buf, descriptors);
        await face.load();
        document.fonts.add(face);
        return face;
      } catch {
        return null;
      }
    };

    const load = async () => {
      const links: HTMLLinkElement[] = [];
      const faces: FontFace[] = [];

      await Promise.all(
        fonts.map(async (font) => {
          switch (font.type) {
            case "hosted-css": {
              try {
                const stylesheet = acquireHostedFontStylesheet(font.cssUrl);
                releaseStylesheets.push(stylesheet.release);
                await stylesheet.ready;
                if (canceled) return;
                await waitForHostedFontFaces(font.name, font.variants);
                markLoaded(font.name);
              } catch (error) {
                console.warn(`Could not load Adobe font ${font.name}`, error);
                if (!canceled)
                  toast({
                    id: `adobe-font-${font.name}`,
                    title: "Couldn't load Adobe font",
                    message: `Check the Adobe web project for ${font.name} and reload the page.`,
                    variant: "error",
                  });
              }
              break;
            }
            case "google": {
              const link = document.createElement("link");
              link.rel = "stylesheet";
              link.href = constructGoogleFontUrl(font.name, font.variants);
              link.setAttribute("data-ht-google-font", font.name);
              document.head.appendChild(link);
              links.push(link);
              // The <link> registers @font-face rules in document.fonts; wait
              // for them so readiness reflects the actually-rendered glyphs.
              try {
                await document.fonts.load(`1em "${font.name}"`);
              } catch {
                // Ignore — marked loaded below so a flaky font can't block
                // the editor/save forever.
              }
              markLoaded(font.name);
              break;
            }

            case "file": {
              // Register under the parsed typographic family (with
              // weight/style descriptors) when known, plus the
              // filename-derived alias — markup may reference either. The
              // bytes are fetched once (shared cache).
              const source = fileFontSource(font);
              if (font.family) {
                const familyFace = await loadFace(
                  font.family,
                  source,
                  fileFaceDescriptors(font),
                );
                if (familyFace) faces.push(familyFace);
                markLoaded(font.family);
              }
              const aliasDescriptors = fileFaceDescriptors(font);
              for (const alias of fileFontAliases(font)) {
                const face = await loadFace(alias, source, aliasDescriptors);
                // Track every created face so the cancel-path cleanup below can delete
                // it. `loadFace` already added it to document.fonts, so skipping the push
                // when canceled would orphan the face in the global registry.
                if (face) faces.push(face);
                markLoaded(alias);
              }
              // Mark loaded even on failure: a broken font should fall back,
              // not permanently block the canvas/save.
              markLoaded(font.name);
              break;
            }

            case "custom": {
              await Promise.all(
                font.variants.map(async (variant) => {
                  const face = await loadFace(
                    font.name,
                    { url: variant.url },
                    {
                      weight: variant.weight,
                      style: variant.style,
                    },
                  );
                  // Track every created face so the cancel-path cleanup below can delete
                  // it. `loadFace` already added it to document.fonts, so skipping the push
                  // when canceled would orphan the face in the global registry.
                  if (face) faces.push(face);
                }),
              );
              markLoaded(font.name);
              break;
            }

            case "web-safe":
              // Available by default — no face to load.
              markLoaded(font.name);
              break;
          }
        }),
      );

      if (canceled) {
        for (const link of links) link.remove();
        for (const face of faces) document.fonts.delete(face);
        return;
      }

      linksRef.current = links;
      facesRef.current = faces;

      await document.fonts.ready;
      if (!canceled) {
        setFontsReady(true);
      }
    };

    void load();

    return () => {
      canceled = true;
      for (const release of releaseStylesheets) release();
      cleanup();
    };
  }, [fontsKey, fetchBytes, toast]);

  // TODO(eric.lovett): This `biome-ignore` was part of a blanket suppression so that we could
  // turn on the rule. It is probably covering up a real bug. Please fix this or replace this
  // TODO with an explanation of why we don't want all deps listed.
  // biome-ignore lint/correctness/useExhaustiveDependencies: legacy suppression, probably a bug
  const fontOptions = useMemo(() => {
    const seen = new Set<string>();
    const options: { label: string; value: string }[] = [];
    for (const f of optionFonts) {
      const displayName =
        groupFileFontsByFamily && f.type === "file" && f.family
          ? f.family
          : f.name;
      if (!seen.has(displayName)) {
        seen.add(displayName);
        options.push({ label: displayName, value: displayName });
      }
    }
    return options;
  }, [optionFontsKey, groupFileFontsByFamily]);

  // Pool families are appended to `fonts` by `withPlatformFonts`, never to
  // `pickerFonts`. Match on the pool catalog rather than "loaded but not
  // pickable" so retired brand families (also loaded-only) stay out of the
  // picker.
  // biome-ignore lint/correctness/useExhaustiveDependencies: keyed on the serialised lists, same as `fontOptions`
  const globalFontOptions = useMemo(() => {
    const poolKeys = new Set(
      PLATFORM_FONTS.map((fam) => canonicalFamilyKey(fam.family)),
    );
    const pickerKeys = new Set(
      fontOptions.map((option) => canonicalFamilyKey(option.value)),
    );
    const seen = new Set<string>();
    const options: { label: string; value: string }[] = [];
    for (const f of fonts) {
      if (f.type !== "google") continue;
      const key = canonicalFamilyKey(f.name);
      if (!poolKeys.has(key) || pickerKeys.has(key) || seen.has(key)) continue;
      seen.add(key);
      options.push({ label: f.name, value: f.name });
    }
    return options.sort((a, b) => a.label.localeCompare(b.label));
  }, [fontsKey, fontOptions]);

  const value = useMemo<FontInjectionContextValue>(
    () => ({
      fontOptions,
      globalFontOptions,
      fontsReady,
      getFontRulesForMarkup,
      getAllFontRules,
      brandFamilies,
      loadedFamilies,
      fileFontWeightsByName,
      fileAliasToFamily,
    }),
    [
      fontOptions,
      globalFontOptions,
      fontsReady,
      getFontRulesForMarkup,
      getAllFontRules,
      brandFamilies,
      loadedFamilies,
      fileFontWeightsByName,
      fileAliasToFamily,
    ],
  );

  // It's fine for this to get called from useEffect because we're using refs
  function cleanup() {
    for (const link of linksRef.current) {
      link.remove();
    }
    linksRef.current = [];
    for (const face of facesRef.current) {
      document.fonts.delete(face);
    }
    facesRef.current = [];
  }

  return (
    <FontInjectionContext.Provider value={value}>
      {children}
    </FontInjectionContext.Provider>
  );
}

/**
 * Returns font injection data from the nearest FontInjectionProvider.
 */
export function useFontInjection(): FontInjectionContextValue {
  return useContext(FontInjectionContext);
}

/**
 * Whether every brand font referenced by `markup` has loaded for rendering.
 * Use this to gate per-image work (canvas reveal, save buttons) on just the
 * fonts an image actually uses, instead of waiting on the whole brand set
 * (`fontsReady`). It's a pure derivation of provider state, so it's safe to
 * call with a live, changing markup string without causing reload churn.
 */
export function useFontsReadyForMarkup(markup: string): boolean {
  const { brandFamilies, loadedFamilies } = useFontInjection();
  return useMemo(
    () => areReferencedFontsLoaded(markup, brandFamilies, loadedFamilies),
    [markup, brandFamilies, loadedFamilies],
  );
}
